import { useContext, useMemo, useState } from 'react';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  allMetricDescriptions,
  MetricKind,
  specificTagMetrics,
} from '@/webapi/models';
import { centered } from '@/components/use-shared-element';
import { formatEnum } from '@/utils/types';
import { CustomMetric } from '@/features/global-rules/custom-goals/api';
import { isPriceTestExperience } from '@/utils/experience-utils';
import { formatPriceTestGoal } from '@/features/details/price-test-reports/shared';

const MIN_SELECTED_GOALS = 1;
export const MAX_SELECTED_GOALS = 10;

export function useGoalsModal(setIsVisible: (value: boolean) => void) {
  const {
    experienceState: { currentExperience, setTargetingGoals },
    resources,
  } = useContext(EditorContext);
  const customMetrics = resources?.customMetrics || [];

  const toRect = centered(68, 85);

  const [selected, setSelected] = useState<MetricKind[]>(
    currentExperience.goals?.filter((goal) => !goal?.startsWith(`EVENT`)),
  );

  const onSelection = (isSelected: boolean, s: MetricKind) => {
    if (!isSelected && selected.length > MAX_SELECTED_GOALS) {
      return;
    }

    if (isSelected && selected.length === MIN_SELECTED_GOALS) {
      return;
    }

    let clone = [...selected];
    if (!isSelected) {
      clone.push(s);
    } else {
      clone = clone.filter((seg) => seg !== s);
    }
    setSelected(clone);
  };

  const onSave = () => {
    setTargetingGoals(selected);
    setIsVisible(false);
  };

  const allPriceTestGoals = [
    MetricKind.PRODUCT_ATC_RATE,
    MetricKind.PRODUCT_RETURNS,
    MetricKind.PRODUCT_RETURN_PERCENT,
    MetricKind.PRODUCT_CR,
    MetricKind.PRODUCT_PSV,
    MetricKind.PRODUCT_ITEMS_SOLD,
    MetricKind.PRODUCT_PROFIT,
    MetricKind.PRODUCT_PROFIT_PERCENT,
    MetricKind.PRODUCT_PROFIT_PER_SESSION,
    MetricKind.PRODUCT_TRANSACTIONS,
    MetricKind.PRODUCT_IPP,
    MetricKind.PRODUCT_DETAILS_VIEWS_RATE,
    MetricKind.PRODUCT_REVENUE,
  ].map((m) => ({
    kind: m,
    name: formatPriceTestGoal(m),
    description: allMetricDescriptions?.get(m)?.split(`.`)?.[0],
  }));

  const allGoals = useMemo(
    (): GoalModel[] =>
      [
        MetricKind.REVENUE,
        MetricKind.TRANSACTIONS,
        MetricKind.AVG_ORDER_VALUE,
        MetricKind.PER_SESSION_VALUE,
        MetricKind.SIGNUPS,
        MetricKind.CONVERSION_RATE,
        MetricKind.ADD_TO_CART_RATE,
        MetricKind.PROFIT,
        MetricKind.PROFIT_PER_SESSION,
        MetricKind.RETURNS,
        MetricKind.RETURNS_PER_SESSION,
        MetricKind.PRODUCT_DETAILS_VIEWS_RATE,
        MetricKind.CHECKOUT_RATE,
        ...handleCustomGoalsList(customMetrics),
        MetricKind.SUBSCRIPTION_RATE,
        MetricKind.SUBSCRIPTIONS,
        MetricKind.SUBSCRIPTION_FIRST_ORDER,
        MetricKind.SUBSCRIPTION_FIRST_ORDER_RATE,
        MetricKind.BOUNCE_RATE,
        ...specificTagMetrics(currentExperience?.tag),
      ].map((m) => ({
        kind: m,
        name: formatEnum(m),
        description: allMetricDescriptions?.get(m)?.split(`.`)?.[0],
      })),
    [currentExperience?.tag],
  );

  const isPriceTest = isPriceTestExperience(currentExperience);

  const [isPriceTestModal, setIsPriceTestModal] = useState(false);

  return {
    selected,
    onSave,
    onSelection,
    toRect,
    allGoals,
    allPriceTestGoals,
    isPriceTest,
    customMetrics: getCustomMetricsMap(customMetrics),
    isPriceTestModal,
    setIsPriceTestModal,
  };
}

export interface GoalModel {
  kind: MetricKind;
  name: string;
  description: string;
}

export const getCustomMetricsMap = (customMetrics: CustomMetric[]) =>
  (customMetrics || []).reduce(
    (p, c) => p.set(c.id, c.name),
    new Map<number, string>(),
  );

const handleCustomGoalsList = (customMetrics: CustomMetric[]) =>
  customMetrics.flatMap((m) => {
    if (m.id === 1) {
      return [MetricKind.CUSTOM_1, MetricKind.CUSTOM_1_RATE];
    }
    if (m.id === 2) {
      return [MetricKind.CUSTOM_2, MetricKind.CUSTOM_2_RATE];
    }
    if (m.id === 3) {
      return [MetricKind.CUSTOM_3, MetricKind.CUSTOM_3_RATE];
    }
    return [];
  });
